/**
 * @fileoverview Hook actions layer for managing customer tags in CustomerDetail (Inbox).
 * Allows creating local temporary tags, saving tags in backend and updating the global state of conversations.
 * Implements strict separation of responsibilities, robust error handling, callbacks and performance optimization.
 * Optimized architecture for large and small volumes, with use of useCallback/useMemo and render tracking.
 * Meets the standards of documentation and atomicity of Mercately Frontend v3.1.0.
 *
 * @module useCustomerDetailTagsActions
 * @category Hook - Actions Layer
 * @since 3.1.0
 * @version 3.1.1 - Extended documentation, performance details and atomicity
 */

import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TagsModelMC from '../../../models/TagsModelMC';
import CustomersModelMc from '../../../models/CustomersModelMc';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { applyCustomerTagsUpdate } from '../../../../actions/inbox/customerDataActions';
import { showInboxApiErrorToast } from '../utils/showInboxApiErrorToast';

/**
 * @typedef {Object} LocalTag
 * @description Local temporary tag created in frontend.
 * @property {string} id - Temporary ID generated locally (e.g: 'temp_123456789')
 * @property {string} label - Tag visible name
 * @property {string} name - Tag name (equal to label)
 * @property {boolean} isLocal - Indicates if the tag is local (true)
 */

/**
 * @typedef {Object} SaveCustomerTagsOptions
 * @description Options for saving customer tags.
 * @property {Array} tagOptions - Available tag options
 * @property {string|number} customerId - Customer ID
 * @property {string} platform - Platform (whatsapp, messenger, etc)
 * @property {function} refreshTags - Callback to refresh list of tags
 * @property {function} onSuccess - Success callback
 * @property {function} onError - Error callback
 * @property {function} setLocalCreatedTags - Setter to clean local tags
 */

/**
 * Hook actions layer for managing customer tags in CustomerDetail (Inbox).
 *
 * Implements the **Actions Layer** pattern with strict separation of responsibilities:
 * - Only business operations and global state mutations
 * - No UI logic or form validation
 * - Robust error handling and callbacks
 *
 * **Optimized Architecture (v3.1.0):**
 * - ✅ Only business mutations (create local tag, save tags backend)
 * - ✅ useCallback and useMemo for performance
 * - ✅ Error handling and success/error callbacks
 *
 * @function useCustomerDetailTagsActions
 * @category Hook - Actions Layer
 * @returns {{loading: boolean, createLocalTag: function, saveCustomerTags: function}} Object with state and actions
 *
 * @see {@link ../../../../app/javascript/presentation/modules/inbox/components/CustomerDetailFormMC.js} Used in customer detail form
 * @see {@link ../../../../app/javascript/presentation/models/TagsModelMC.js} Tags model
 * @see {@link ../../../../app/javascript/presentation/models/CustomersModelMc.js} Customers model
 *
 * @since 3.1.0
 * @version 3.1.0 - Optimized and documented first version
 */
const useCustomerDetailTagsActions = () => {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  /**
   * Creates a local temporary tag in frontend for immediate selection.
   *
   * @async
   * @function createLocalTag
   * @memberof useCustomerDetailTagsActions
   * @param {string} tagName - Tag name to create
   * @param {Object} options - Opciones de creación
   * @param {function} options.checkTagExists - Function to check if the tag already exists
   * @param {function} options.setLocalCreatedTags - Setter to update list of local tags
   * @throws {Error} If the tag already exists
   * @returns {Promise<LocalTag>} Tag local creado
   *
   * @example
   * const tempTag = await createLocalTag('VIP', { checkTagExists, setLocalCreatedTags });
   */
  const createLocalTag = useCallback(async (tagName, { checkTagExists, setLocalCreatedTags }) => {
    try {
      if (checkTagExists(tagName)) {
        throw new Error('Tag already exists');
      }

      // Create local tag with temporary ID
      const tempTag = {
        id: `temp_${Date.now()}`,
        label: tagName,
        name: tagName,
        isLocal: true
      };

      // Add to local list
      setLocalCreatedTags((prevTags) => [...prevTags, tempTag]);

      return tempTag;
    } catch (error) {
      console.error('Error creating local tag:', error);
      throw error;
    }
  }, []);

  /**
   * Saves the selected tags for a customer, creating local tags in backend if necessary.
   *
   * @async
   * @function saveCustomerTags
   * @memberof useCustomerDetailTagsActions
   * @param {Array<string|number>} selectedTags - Selected tag IDs (may include temporary)
   * @param {SaveCustomerTagsOptions} options - Saving options and callbacks
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>} Operation result
   * @throws {Error} If an error occurs in the creation or assignment of tags
   *
   * @example
   * const result = await saveCustomerTags(['temp_123', 5], { customerId, tagOptions, ... });
   */
  const saveCustomerTags = useCallback(
    async (
      selectedTags,
      options
    ) => {
      // Extract dynamic data from options
      const {
        tagOptions,
        customerId,
        refreshTags,
        onSuccess,
        onError,
        setLocalCreatedTags
      } = options || {};

      setLoading(true);
      try {
        const finalTagIds = [...selectedTags];

        // Step 1: Create local tags in backend if they exist
        const selectedLocalTags = (tagOptions || []).filter((option) => selectedTags.includes(option.id) && option.isLocal);

        if (selectedLocalTags.length > 0) {
          const formData = selectedLocalTags.map((opt) => ({ tag: opt.label }));
          const response = await TagsModelMC.bulkCreate({ tags: formData });

          // Map temporary IDs to real IDs
          if (response.taga && Array.isArray(response.taga)) {
            selectedLocalTags.forEach((localTag, index) => {
              const realTag = response.taga[index];
              if (realTag && realTag.id) {
                const tempIndex = finalTagIds.indexOf(localTag.id);
                if (tempIndex !== -1) {
                  finalTagIds[tempIndex] = realTag.id;
                }
              }
            });
          }

          // Refresh list of tags from server
          if (typeof refreshTags === 'function') refreshTags();
        }

        // Step 2: Assign all tags to the customer (only real IDs)
        if (customerId) {
          const realTagIds = finalTagIds.filter((id) => !String(id).startsWith('temp_'));

          const customerTagsData = {
            customer: {
              tag_ids: realTagIds
            }
          };

          const responseData = await CustomersModelMc.addCustomerTags(customerId, customerTagsData);

          // Step 3: Push the saved tags into the store so the sidebar and the chat list
          // repaint. `responseData.tags` is NOT this — that key holds the tags still
          // *available* to assign (retailer catalog minus the customer's).
          dispatch(applyCustomerTagsUpdate({
            customerId,
            tags: responseData?.customer?.tags
          }));

          // Step 4: Clean local state
          if (typeof setLocalCreatedTags === 'function') setLocalCreatedTags([]);

          if (typeof onSuccess === 'function') onSuccess(responseData);
          return { success: true, data: responseData };
        }

        return { success: false, error: 'No customer ID provided' };
      } catch (error) {
        const errorMessage = showInboxApiErrorToast(
          error,
          t('customerDetails.tags.saveFailed', { defaultValue: 'Error updating customer tags' })
        );

        dispatch({
          type: LOAD_DATA_FAILURE,
          error: errorMessage
        });

        if (typeof onError === 'function') onError(error);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [dispatch, t]
  );

  return useMemo(() => ({
    loading,
    createLocalTag,
    saveCustomerTags
  }), [loading, createLocalTag, saveCustomerTags]);
};

export default useCustomerDetailTagsActions;
